import { Flex, Box, ThemeUIStyleObject } from 'theme-ui';
import React, { useState, useEffect } from 'react';
import useNavigationQuery from '~/hooks/graphql/queries/use-navigation';
import SiteWideBanner from '~/components/SiteWideBanner';
import LinkRenderer from '~/components/Generic/LinkRenderer';
import useNavigationHeight from '~/context/NavigationContext/useNavigationHeight';
import CartButton from '../CartButton';
import HamburgerButton from '../HamburgerButton';
import { alpha } from '@theme-ui/color';
import Logo from '~/assets/images/logos/logo.svg';
import MobileDropdown from '../Dropdown/MobileDropdown';
import { isContentfulPage, isNavigationItem } from '~/utils/nav';

type Props = {
  openCart: () => void;
  navColor?: boolean;
  itemCount: number | null;
  isAtTopOfPage: boolean;
  transparentNav?: boolean;
  forwardSx?: ThemeUIStyleObject;
  isMenuOpen: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const NavBar = React.forwardRef<HTMLDivElement, Props>(
  (
    { openCart, navColor = false, itemCount, isAtTopOfPage, transparentNav, forwardSx = {}, isMenuOpen, setIsMenuOpen },
    ref
  ) => {
    const [oneDropdownOpen, setOneDropdownOpen] = useState(false);
    const [touchStarted, setTouchStarted] = useState(false);
    const { main, mobileDropdown } = useNavigationQuery();
    const [hoveredLink, setHoveredLink] = useState<string | null>(null);
    const [hasScrolled, setHasScrolled] = useState(false);

    const { navHeight, forceRecalculate } = useNavigationHeight();
    const color = navColor ? 'black' : 'white';

    useEffect(() => {
      const handleScroll = () => {
        const scrollTop = window.scrollY;
        setHasScrolled(scrollTop > navHeight / 3);
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, [navHeight]);

    useEffect(() => {
      if (oneDropdownOpen) {
        setIsMenuOpen(false);
      }
    }, [oneDropdownOpen, setIsMenuOpen]);

    const toggleMenu = () => {
      setIsMenuOpen((prev) => !prev);
    };

    return (
      <header
        ref={ref}
        sx={{
          '--logo-width': ['7.8rem', '11rem', '10.5rem'],
          position: transparentNav ? 'fixed' : 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 98,
          transform: 'translate3d(0,0,0)', // Force hardware acceleration, safari quirk, please don't remove
          ...forwardSx,
        }}
      >
        <SiteWideBanner setInnerRef={forceRecalculate} />
        <Box
          as="nav"
          variant="navContainer"
          sx={(t) => ({
            display: 'grid',
            gridTemplateColumns: ['max-content 1fr max-content', null, 'var(--logo-width) 1fr max-content'],
            justifyContent: 'space-between',
            alignItems: 'stretch',
            height: 'var(--nav-height)',
            py: 0,
            pl: 'var(--gutter-width)',
            pr: ['0.8rem', 'var(--gutter-width)'], // mobile already has 16px padding from the hamburger button
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: [alpha('backgroundGrey', 0.6)(t), null, alpha('backgroundGrey', 0.8)(t)],
              backdropFilter: 'blur(20px)',
              zIndex: -1,
            },
            transition: 'background-color 0.3s ease-in-out',
          })}
        >
          {/* @ts-ignore */}
          <LinkRenderer
            to="/"
            sx={{
              display: 'flex',
              height: '100%',
              width: 'var(--logo-width)',
              alignItems: 'center',
              svg: {
                width: '100%',
                height: '100%',
              },
            }}
          >
            <Logo />
          </LinkRenderer>

          <nav
            sx={{
              display: ['none', null, 'flex'],
              justifyContent: 'center',
            }}
            aria-label="Site navigation"
          >
            <Flex
              as="ul"
              aria-label="header"
              sx={{
                variant: 'list.bare',
                flexFlow: 'row nowrap',
                alignItems: 'stretch',
                fontWeight: 'medium',
                textTransform: 'uppercase',
                fontSize: 2,
                'li::before': {
                  content: '""',
                },
                'li > a': {
                  position: 'relative',
                  fontSize: '1.4rem',
                  color: transparentNav ? color : 'black',
                  transition: 'color 0.3 ease-in-out',
                },

                'li:hover > a::before': {
                  '@media (hover: hover)': {
                    width: '100%',
                    backgroundColor: transparentNav ? color : 'black',
                  },
                },
                'li > a::before': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-6px',
                  width: 0,
                  height: '2px',
                  left: '50%',
                  backgroundColor: color,
                  transform: 'translateX(-50%)',
                  transition: 'width 0.3s ease-in-out',
                },
                'li > a.is-active::before': {
                  width: hoveredLink ? 0 : '100%',
                  backgroundColor: 'black',
                },
                'li.is-peeking > a::before': {
                  width: '100%',
                },
                'li:not(.is-hover-locked):hover > nav': {
                  transform: `translateY(calc(${navHeight}px))`,
                  transition: '0.3s ease-in-out', // Intro

                  ' > * > ul': {
                    opacity: 1,
                    transition: 'opacity 0.3s 0.3s ease-in-out', // Intro
                  },
                },
              }}
            >
              {main?.map((item) => {
                if (!item) return null;
                // Do not render home link in desktop nav
                if (isContentfulPage(item) && item.slug === 'home') return null;
                if (isNavigationItem(item)) {
                  return (
                    <Flex
                      as="li"
                      onPointerEnter={() => {
                        setHoveredLink(isContentfulPage(item) ? item.slug ?? '' : item.innerLink ?? '');
                      }}
                      onPointerLeave={() => {
                        setHoveredLink(null);
                      }}
                      className={`${
                        hoveredLink === (isContentfulPage(item) ? item.slug : item.innerLink) ? 'is-peeking' : ''
                      }`}
                      sx={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        pr: '1.2rem',
                        '&:not(:first-of-type)': {
                          pl: '1.2rem',
                        },
                      }}
                      key={item.id}
                    >
                      {/* @ts-ignore */}
                      <LinkRenderer
                        type={item.__typename}
                        slug={isContentfulPage(item) ? item.slug : item.innerLink}
                        title={isContentfulPage(item) ? item.title : item.text}
                        activeClassName="is-active"
                        partiallyActive
                        locationDetail="nav"
                        sx={{ mb: ['-5px'] }}
                      >
                        {isContentfulPage(item) ? item.title : item.text}
                      </LinkRenderer>
                    </Flex>
                  );
                }
                return null;
              })}
            </Flex>
          </nav>
          <nav
            sx={{
              display: ['flex', null, 'none'],
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Mobile dropdown navigation"
          >
            {mobileDropdown?.map((item) => {
              if (!item) return null;
              return (
                <MobileDropdown
                  key={item.title}
                  dropdown={item}
                  hasScrolled={hasScrolled}
                  isMenuOpen={isMenuOpen}
                  setOneDropdownOpen={setOneDropdownOpen}
                />
              );
            })}
          </nav>

          <Flex
            as="nav"
            aria-label="Site navigation"
            sx={{
              flexFlow: 'row nowrap',
              justifyContent: 'flex-end',
              alignItems: 'center',
              '> a:not(:last-of-type)': { marginRight: 2 },
              svg: { verticalAlign: 'middle' },
              gap: [0, '1.6rem'],
            }}
          >
            <CartButton
              handleOpenCart={openCart}
              shouldSwapColor={Boolean(transparentNav && isAtTopOfPage)}
              color={color}
              itemCount={itemCount}
            />
            <HamburgerButton
              color={color}
              isAtTopOfPage={isAtTopOfPage}
              toggleMenu={toggleMenu}
              setTouchStarted={setTouchStarted}
              isMenuOpen={isMenuOpen}
              touchStarted={touchStarted}
              transparentNav={Boolean(transparentNav)}
            />
          </Flex>
        </Box>
      </header>
    );
  }
);

export default React.memo(NavBar);
